/**
 * Narrowing helpers for API response bodies, and the FS-10 list envelope.
 *
 * Every response is narrowed from `unknown` at the API boundary, with no unchecked cast: a body
 * whose shape diverges from its contract throws an `ApiContractError` naming the field, so the
 * divergence fails loudly where it arrives instead of surfacing as `undefined` inside a render
 * (F26-b WI-1.3 Decision 2). The pattern is `src/features/email/inboxApi.ts`'s `require*` helpers and
 * `apiClient.ts`'s `toProblem`, moved here because three folders — `src/features/pricing`,
 * `src/features/accounts` and `src/lib/buyingStandards` — now narrow responses
 * (contract client-side fact 10). The users and email features narrow their own fields with their own
 * error classes, `UsersContractError` and `MailboxContractError`, both subclasses of `ApiContractError`.
 *
 * **The list envelope is transcribed from `.forge/plans/F26-b/F26-b-Wave-1-contract.md`,
 * `### ListResponse<T>`** — FS-10's shape, unchanged: `items` is never `null`, `page.size` is the size
 * asked for (not the number of items), and `page.totalPages` is `0` for an empty set.
 */

/** A response body that does not match its contract. The message names the field. */
export class ApiContractError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ApiContractError';
    }
}

/**
 * Refuses an omitted key: the contract sends an absent value as a present `null`, never an omitted key
 * (§ Envelope & Convention Rules), so `undefined` is a contract break.
 */
export function requirePresent(value: unknown, field: string): void {
    if (value === undefined) {
        throw new ApiContractError(`${field} must be present, null when it does not apply`);
    }
}

/** A value from a closed set of string members, or throw naming the field and the members. */
export function requireOneOf<T extends string>(value: unknown, field: string, members: readonly T[]): T {
    const member = members.find((candidate) => candidate === value);
    if (member === undefined) {
        throw new ApiContractError(`${field} must be one of ${members.join(', ')}`);
    }
    return member;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** An object, or throw. */
export function requireRecord(value: unknown, field: string): Record<string, unknown> {
    if (!isRecord(value)) {
        throw new ApiContractError(`${field} must be an object`);
    }
    return value;
}

/**
 * Refuse an object whose keys are not exactly `keys` — a contract's "exactly these members": a member
 * the contract does not declare, or a declared one that is absent, throws naming the field.
 */
export function requireExactKeys(
    value: Record<string, unknown>,
    keys: readonly string[],
    field: string,
): void {
    for (const key of Object.keys(value)) {
        if (!keys.includes(key)) {
            throw new ApiContractError(`${field} has a member the contract does not declare: ${key}`);
        }
    }
    for (const key of keys) {
        if (!(key in value)) {
            throw new ApiContractError(`${field}.${key} must be present`);
        }
    }
}

export function requireString(value: unknown, field: string): string {
    if (typeof value !== 'string') {
        throw new ApiContractError(`${field} must be a string`);
    }
    return value;
}

/**
 * A string or `null`. An omitted key arrives as `undefined` and is refused: the contract sends an
 * absent value as a present `null`, never an omitted key (§ Envelope & Convention Rules).
 */
export function requireNullableString(value: unknown, field: string): string | null {
    if (value === null) {
        return null;
    }
    if (typeof value !== 'string') {
        throw new ApiContractError(`${field} must be a string or null, present either way`);
    }
    return value;
}

/** A whole number. Identifiers are JSON numbers, never strings (F26-b contract § Identifiers). */
export function requireInteger(value: unknown, field: string): number {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
        throw new ApiContractError(`${field} must be an integer`);
    }
    return value;
}

/**
 * A whole number or `null`. An omitted key is refused, as `requireNullableString` refuses one: the
 * contract sends the key either way, so its absence is a contract break, not a `null`.
 */
export function requireNullableInteger(value: unknown, field: string): number | null {
    if (value === null) {
        return null;
    }
    if (value === undefined) {
        throw new ApiContractError(`${field} must be an integer or null, present either way`);
    }
    return requireInteger(value, field);
}

/** A finite JSON number — a decimal such as a price, which arrives as a number, not a string. */
export function requireNumber(value: unknown, field: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new ApiContractError(`${field} must be a number`);
    }
    return value;
}

/** A finite number or `null`. An omitted key is refused, as `requireNullableInteger` refuses one. */
export function requireNullableNumber(value: unknown, field: string): number | null {
    if (value === null) {
        return null;
    }
    if (value === undefined) {
        throw new ApiContractError(`${field} must be a number or null, present either way`);
    }
    return requireNumber(value, field);
}

export function requireBoolean(value: unknown, field: string): boolean {
    if (typeof value !== 'boolean') {
        throw new ApiContractError(`${field} must be a boolean`);
    }
    return value;
}

/**
 * An ISO-8601 instant, kept as it arrives; `formatInstantDate` reads it in Colombo. A value `Date`
 * cannot parse is refused here rather than shown as *Invalid Date*.
 */
export function requireInstant(value: unknown, field: string): string {
    const text = requireString(value, field);
    if (Number.isNaN(new Date(text).getTime())) {
        throw new ApiContractError(`${field} must be an ISO-8601 instant`);
    }
    return text;
}

const BUSINESS_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True when `text` is a real calendar date in `yyyy-MM-dd` form (not `2026-02-30`). */
export function isBusinessDate(text: string): boolean {
    const match = BUSINESS_DATE.exec(text);
    if (match === null) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * A Colombo business date, `yyyy-MM-dd` — a calendar date, never an instant (contract fact 4). The
 * string is kept as it arrives; formatting works on the date itself (`src/lib/format/businessDate.ts`).
 */
export function requireBusinessDate(value: unknown, field: string): string {
    if (typeof value !== 'string' || !isBusinessDate(value)) {
        throw new ApiContractError(`${field} must be a yyyy-MM-dd date`);
    }
    return value;
}

/**
 * A business date or `null` — `validBefore` while a range is open-ended. An omitted key is refused
 * (`requirePresent`): the contract sends the key either way.
 */
export function requireNullableBusinessDate(value: unknown, field: string): string | null {
    requirePresent(value, field);
    return value === null ? null : requireBusinessDate(value, field);
}

/** An array whose every element `toElement` narrows. Never `null` (F26-b contract § Collections). */
export function requireArray<T>(
    value: unknown,
    field: string,
    toElement: (element: unknown, elementField: string) => T,
): readonly T[] {
    if (!Array.isArray(value)) {
        throw new ApiContractError(`${field} must be an array, never null`);
    }
    return value.map((element, index) => toElement(element, `${field}[${index}]`));
}

/** The FS-10 `PageMeta`. `size` is the size ASKED for, not the number of items on this page. */
export interface PageMeta {
    /** The 1-based page returned. */
    readonly number: number;
    readonly size: number;
    /** The exact count of the filtered set. */
    readonly totalItems: number;
    /** `0` when the set is empty. */
    readonly totalPages: number;
}

/** The FS-10 `ListResponse<T>` — the `200` body of every list read. */
export interface ListResponse<T> {
    /** `[]` on an empty set and on a page past the last — never `null`. */
    readonly items: readonly T[];
    readonly page: PageMeta;
}

export function toPageMeta(value: unknown): PageMeta {
    const page = requireRecord(value, 'page');
    return {
        number: requireInteger(page.number, 'page.number'),
        size: requireInteger(page.size, 'page.size'),
        totalItems: requireInteger(page.totalItems, 'page.totalItems'),
        totalPages: requireInteger(page.totalPages, 'page.totalPages'),
    };
}

/** Narrow a list body, mapping each item through `toItem`. */
export function toListResponse<T>(
    body: unknown,
    toItem: (item: unknown, field: string) => T,
): ListResponse<T> {
    const record = requireRecord(body, 'the body');
    return {
        items: requireArray(record.items, 'items', toItem),
        page: toPageMeta(record.page),
    };
}
